import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
	Autocomplete,
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	Grid,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {
	Clear as ClearIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	FilterList as FilterListIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
	IReceiptFilters,
	PaymentReceivedRelationTypeEnum,
	PaymentReceivedPlatformTypeEnum,
	SegmentEnum,
	segmentTypes,
} from '../../../types';
import { countriesWithAgencyConfig } from 'features/auth/constants';
import { CIVIL_DATE_PRESET_LABELS, CivilDatePreset, getDateRangeFromPreset } from 'features/common/helpers/datePresets';
import { ReceiptsQuickDateBar } from '../ReceiptsQuickDateBar';

// ─── Opciones estáticas ───────────────────────────────────────────────────────

interface BUOption {
	agencyId: number;
	label: string;
	fullName: string;
	icon: string;
}

const BU_OPTIONS: BUOption[] = countriesWithAgencyConfig.map((c) => ({
	agencyId: c.agencyId,
	label: c.id as string,
	fullName: c.countryName,
	icon: c.agencyIcon,
}));

const SEGMENT_OPTIONS = segmentTypes.map((s) => ({ id: s.id, label: s.name }));

const RELATION_OPTIONS = [
	{ id: PaymentReceivedRelationTypeEnum.WireTransferPayment, label: 'Transferencia' },
	{ id: PaymentReceivedRelationTypeEnum.CashPayment, label: 'Efectivo' },
	{ id: PaymentReceivedRelationTypeEnum.MVOrderTransaction, label: 'C2A / Tarjeta' },
	{ id: PaymentReceivedRelationTypeEnum.VNOrderTransaction, label: 'VN Transacción' },
	{ id: PaymentReceivedRelationTypeEnum.MVHotelReserve, label: 'MV Hotel' },
	{ id: PaymentReceivedRelationTypeEnum.VNFlightReserve, label: 'VN Vuelo' },
	{ id: PaymentReceivedRelationTypeEnum.VNHotelReserveMV, label: 'VN Hotel MV' },
];

const PLATFORM_OPTIONS = [
	{ id: PaymentReceivedPlatformTypeEnum.WireTransfer, label: 'Wire Transfer' },
	{ id: PaymentReceivedPlatformTypeEnum.Cash, label: 'Efectivo' },
	{ id: PaymentReceivedPlatformTypeEnum.Bancard, label: 'Bancard' },
	{ id: PaymentReceivedPlatformTypeEnum.SiemprePago, label: 'SiemprePago' },
	{ id: PaymentReceivedPlatformTypeEnum.Wompi, label: 'Wompi' },
	{ id: PaymentReceivedPlatformTypeEnum.ItauPoints, label: 'Puntos Itaú' },
	{ id: PaymentReceivedPlatformTypeEnum.ATCRedEnlace, label: 'ATC Red Enlace' },
	{ id: PaymentReceivedPlatformTypeEnum.PaymentSimulator, label: 'Tarjeta contra Aerolinea' },
];

const CURRENCY_OPTIONS = ['USD', 'UYU', 'PYG', 'COP', 'ARS', 'BRL'];

interface CardStampOption {
	value: string;
	label: string;
}

const CARD_STAMP_OPTIONS: CardStampOption[] = [
	{ value: 'VISA', label: 'Visa' },
	{ value: 'MASTERCARD', label: 'Mastercard' },
	{ value: 'AMEX', label: 'American Express' },
	{ value: 'OCA', label: 'OCA' },
	{ value: 'DINERS', label: 'Diners Club' },
	{ value: 'CABAL', label: 'Cabal' },
	{ value: 'UNKNOWN', label: 'Sin identificar' },
];

const FILTER_DEBOUNCE_MS = 500;

const normalizeCardStamp = (value: string): string => value.trim().toUpperCase();

const normalizeCardStampValues = (values: string[]): string[] =>
	Array.from(new Set(values.map(normalizeCardStamp).filter(Boolean)));

export const getCardStampLabel = (value: string): string =>
	CARD_STAMP_OPTIONS.find((x) => x.value === normalizeCardStamp(value))?.label ?? value;

const getCardStampEmoji = (value: string): string => {
	switch (normalizeCardStamp(value)) {
		case 'VISA':
			return '💳';
		case 'MASTERCARD':
			return '🔴';
		case 'AMEX':
			return '💎';
		case 'OCA':
			return '🔵';
		case 'DINERS':
			return '🍽️';
		case 'CABAL':
			return '🟢';
		case 'UNKNOWN':
			return '❔';
		default:
			return '💳';
	}
};

const CardStampIcon = ({ value }: { value: string }): JSX.Element => (
	<Box
		component="span"
		sx={{
			width: 20,
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: 16,
			lineHeight: 1,
		}}
	>
		{getCardStampEmoji(value)}
	</Box>
);

interface RelationOption {
	id: PaymentReceivedRelationTypeEnum;
	label: string;
}
interface PlatformOption {
	id: PaymentReceivedPlatformTypeEnum;
	label: string;
}
interface SegmentOption {
	id: SegmentEnum;
	label: string;
}
type ReconciliationFilter = 'all' | 'reconciled' | 'unreconciled';
type ReceiptFilter = 'all' | 'yes' | 'no';

interface LocalFilters {
	saleOrderId: string;
	receiptNumber: string;
	dealId: string;
	passengerName: string;
	reference: string;
	observations: string;
	dateFrom: Date | null;
	dateTo: Date | null;
	reconciledFrom: Date | null;
	reconciledTo: Date | null;
	amountFrom: string;
	amountTo: string;
	bus: BUOption[];
	segments: SegmentOption[];
	relationTypes: RelationOption[];
	platformTypes: PlatformOption[];
	currencies: string[];
	bankName: string;
	cardStamps: string[];
	reconciliation: ReconciliationFilter;
	receipt: ReceiptFilter;
}

const defaultFilters: LocalFilters = {
	saleOrderId: '',
	receiptNumber: '',
	dealId: '',
	passengerName: '',
	reference: '',
	observations: '',
	dateFrom: null,
	dateTo: null,
	reconciledFrom: null,
	reconciledTo: null,
	amountFrom: '',
	amountTo: '',
	bus: [],
	segments: [],
	relationTypes: [],
	platformTypes: [],
	currencies: [],
	bankName: '',
	cardStamps: [],
	reconciliation: 'all',
	receipt: 'all',
};

const toDateString = (d: Date | null): string | undefined => (d ? format(d, 'yyyy-MM-dd') : undefined);

const toNum = (s: string): number | undefined => {
	const n = parseFloat(s);
	return isNaN(n) ? undefined : n;
};

interface DebouncedTextFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}

const DebouncedTextField = ({ label, value, onChange, placeholder }: DebouncedTextFieldProps): JSX.Element => {
	const [draft, setDraft] = useState(value);
	const debouncerRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		setDraft(value);
	}, [value]);

	useEffect(
		() => () => {
			if (debouncerRef.current) clearTimeout(debouncerRef.current);
		},
		[],
	);

	const handleChange = (next: string) => {
		setDraft(next);
		if (debouncerRef.current) clearTimeout(debouncerRef.current);
		debouncerRef.current = setTimeout(() => onChange(next.trim()), FILTER_DEBOUNCE_MS);
	};

	return (
		<TextField
			fullWidth
			size="small"
			label={label}
			placeholder={placeholder}
			value={draft}
			onChange={(e) => handleChange(e.target.value)}
		/>
	);
};

interface Props {
	onFilter: (filters: IReceiptFilters) => void;
}

export const ClientPaymentsFilters = ({ onFilter }: Props): JSX.Element => {
	const location = useLocation();
	const dealIdFromURL = new URLSearchParams(location.search).get('dealId') ?? '';
	const [filters, setFilters] = useState<LocalFilters>({ ...defaultFilters, dealId: dealIdFromURL });
	const [creationDatePreset, setCreationDatePreset] = useState<CivilDatePreset | ''>('');
	const [expanded, setExpanded] = useState(false);
	const prevRef = useRef<string>('');
	const onFilterRef = useRef(onFilter);

	useEffect(() => {
		onFilterRef.current = onFilter;
	}, [onFilter]);

	useEffect(() => {
		const out: IReceiptFilters = {};
		if (filters.saleOrderId.trim()) out.saleOrderId = filters.saleOrderId.trim();
		if (filters.receiptNumber.trim()) out.receiptNumber = filters.receiptNumber.trim();
		if (filters.dealId.trim()) {
			const n = parseInt(filters.dealId.trim(), 10);
			if (!isNaN(n)) out.dealId = n;
		}
		if (filters.passengerName.trim()) out.passengerName = filters.passengerName.trim();
		if (filters.reference.trim()) out.reference = filters.reference.trim();
		if (filters.observations.trim()) out.observations = filters.observations.trim();
		if (filters.bus.length) out.agencyIds = filters.bus.map((b) => b.agencyId);
		if (filters.segments.length) out.segmentTypes = filters.segments.map((s) => s.id);
		if (filters.relationTypes.length) out.relationTypes = filters.relationTypes.map((r) => r.id);
		if (filters.platformTypes.length) out.platformTypes = filters.platformTypes.map((p) => p.id);
		if (filters.currencies.length) out.currencies = filters.currencies;
		if (filters.bankName.trim()) out.bankName = filters.bankName.trim();
		if (filters.cardStamps.length) out.cardStamps = filters.cardStamps;
		const af = toNum(filters.amountFrom);
		if (af !== undefined) out.amountFrom = af;
		const at = toNum(filters.amountTo);
		if (at !== undefined) out.amountTo = at;
		out.dateFrom = toDateString(filters.dateFrom);
		out.dateTo = toDateString(filters.dateTo);
		out.reconciledFrom = toDateString(filters.reconciledFrom);
		out.reconciledTo = toDateString(filters.reconciledTo);
		if (filters.reconciliation === 'reconciled') out.reconciled = true;
		if (filters.reconciliation === 'unreconciled') out.reconciled = false;
		if (filters.receipt === 'yes') out.issued = true;
		if (filters.receipt === 'no') out.issued = false;

		const str = JSON.stringify(out);
		if (prevRef.current !== str) {
			prevRef.current = str;
			onFilterRef.current(out);
		}
	}, [filters]);

	const set = <K extends keyof LocalFilters>(field: K, value: LocalFilters[K]) =>
		setFilters((prev) => ({ ...prev, [field]: value }));

	const handleCreationDateChange = (field: 'dateFrom' | 'dateTo', value: Date | null) => {
		setCreationDatePreset('');
		set(field, value);
	};

	const handleCreationDatePresetToggle = (preset: CivilDatePreset) => {
		if (creationDatePreset === preset) {
			setCreationDatePreset('');
			setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }));
			return;
		}

		const { from, to } = getDateRangeFromPreset(preset);
		setCreationDatePreset(preset);
		setFilters((prev) => ({ ...prev, dateFrom: from, dateTo: to }));
	};

	const handleClear = () => {
		setFilters(defaultFilters);
		setCreationDatePreset('');
		setExpanded(false);
		onFilter({});
	};

	const activeCount = [
		!!filters.saleOrderId.trim(),
		!!filters.receiptNumber.trim(),
		!!filters.dealId.trim(),
		!!filters.passengerName.trim(),
		!!filters.reference.trim(),
		!!filters.observations.trim(),
		filters.bus.length > 0,
		filters.segments.length > 0,
		filters.relationTypes.length > 0,
		filters.platformTypes.length > 0,
		filters.currencies.length > 0,
		!!filters.bankName.trim(),
		filters.cardStamps.length > 0,
		!!filters.amountFrom.trim(),
		!!filters.amountTo.trim(),
		!!filters.dateFrom,
		!!filters.dateTo,
		!!filters.reconciledFrom,
		!!filters.reconciledTo,
		filters.reconciliation !== 'all',
		filters.receipt !== 'all',
	].filter(Boolean).length;

	const buAutocomplete = (
		<Autocomplete
			multiple
			disableCloseOnSelect
			options={BU_OPTIONS}
			value={filters.bus}
			getOptionLabel={(o) => `${o.label} — ${o.fullName}`}
			isOptionEqualToValue={(o, v) => o.agencyId === v.agencyId}
			onChange={(_, val) => set('bus', val)}
			renderOption={(props, option) => (
				<Box component="li" {...props} display="flex" alignItems="center" gap={1}>
					<Box
						component="img"
						src={option.icon}
						alt={option.fullName}
						sx={{
							width: 22,
							height: 16,
							objectFit: 'cover',
							borderRadius: '2px',
							boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
						}}
					/>
					<Box>
						<Typography variant="body2">{option.fullName}</Typography>
						<Typography variant="caption" color="textSecondary">
							{option.label}
						</Typography>
					</Box>
				</Box>
			)}
			renderTags={(value, getTagProps) =>
				value.map((option, index) => (
					<Chip
						{...getTagProps({ index })}
						key={option.agencyId}
						avatar={
							<Box
								component="img"
								src={option.icon}
								alt={option.fullName}
								sx={{
									width: '20px !important',
									height: '14px !important',
									borderRadius: '2px',
									objectFit: 'cover',
								}}
							/>
						}
						label={`${option.label} - ${option.fullName}`}
						size="small"
					/>
				))
			}
			renderInput={(params) => <TextField {...params} label="BU" size="small" />}
		/>
	);

	const cardStampAutocomplete = (
		<Autocomplete
			multiple
			freeSolo
			disableCloseOnSelect
			options={CARD_STAMP_OPTIONS.map((x) => x.value)}
			value={filters.cardStamps}
			getOptionLabel={(option) => getCardStampLabel(option)}
			onChange={(_, val) => set('cardStamps', normalizeCardStampValues(val))}
			renderOption={(props, option) => (
				<Box component="li" {...props} display="flex" alignItems="center" gap={1}>
					<CardStampIcon value={option} />
					<Typography variant="body2">{getCardStampLabel(option)}</Typography>
				</Box>
			)}
			renderTags={(value, getTagProps) =>
				value.map((option, index) => (
					<Chip
						{...getTagProps({ index })}
						key={option}
						icon={<CardStampIcon value={option} />}
						label={getCardStampLabel(option)}
						size="small"
					/>
				))
			}
			renderInput={(params) => (
				<TextField {...params} label="Marca tarjeta" size="small" placeholder="Visa, Mastercard, otra..." />
			)}
		/>
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<ReceiptsQuickDateBar activePreset={creationDatePreset} onPresetToggle={handleCreationDatePresetToggle} />

			<Paper elevation={2} sx={{ p: 2, mb: 2 }}>
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
					<Box display="flex" alignItems="center" gap={1} flexShrink={0}>
						<Badge badgeContent={activeCount} color="primary">
							<FilterListIcon color="action" />
						</Badge>
						<Typography variant="subtitle1" fontWeight={500}>
							Filtros:
						</Typography>
					</Box>

					{activeCount > 0 && (
						<Box display="flex" flexWrap="wrap" gap={0.5} flex={1}>
							{filters.dealId.trim() && (
								<Chip
									label={`Deal: ${filters.dealId.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('dealId', '')}
								/>
							)}
							{creationDatePreset ? (
								<Chip
									label={`Creación: ${CIVIL_DATE_PRESET_LABELS[creationDatePreset]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleCreationDatePresetToggle(creationDatePreset)}
								/>
							) : (
								<>
									{filters.dateFrom && (
										<Chip
											label={`Creación desde: ${format(filters.dateFrom, 'dd/MM/yy')}`}
											size="small"
											color="primary"
											variant="outlined"
											onDelete={() => handleCreationDateChange('dateFrom', null)}
										/>
									)}
									{filters.dateTo && (
										<Chip
											label={`Creación hasta: ${format(filters.dateTo, 'dd/MM/yy')}`}
											size="small"
											color="primary"
											variant="outlined"
											onDelete={() => handleCreationDateChange('dateTo', null)}
										/>
									)}
								</>
							)}
							{filters.bus.map((b) => (
								<Chip
									key={b.agencyId}
									avatar={
										<Box
											component="img"
											src={b.icon}
											alt={b.fullName}
											sx={{
												width: '20px !important',
												height: '14px !important',
												borderRadius: '2px',
												objectFit: 'cover',
											}}
										/>
									}
									label={`BU: ${b.label} - ${b.fullName}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										set(
											'bus',
											filters.bus.filter((x) => x.agencyId !== b.agencyId),
										)
									}
								/>
							))}
							{filters.segments.map((s) => (
								<Chip
									key={s.id}
									label={`Seg: ${s.label}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										set(
											'segments',
											filters.segments.filter((x) => x.id !== s.id),
										)
									}
								/>
							))}
							{filters.relationTypes.map((r) => (
								<Chip
									key={r.id}
									label={`Tipo: ${r.label}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										set(
											'relationTypes',
											filters.relationTypes.filter((x) => x.id !== r.id),
										)
									}
								/>
							))}
							{filters.platformTypes.map((p) => (
								<Chip
									key={p.id}
									label={`Plat: ${p.label}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										set(
											'platformTypes',
											filters.platformTypes.filter((x) => x.id !== p.id),
										)
									}
								/>
							))}
							{filters.currencies.map((c) => (
								<Chip
									key={c}
									label={`Mon: ${c}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										set(
											'currencies',
											filters.currencies.filter((x) => x !== c),
										)
									}
								/>
							))}
							{filters.bankName.trim() && (
								<Chip
									label={`Banco: ${filters.bankName.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('bankName', '')}
								/>
							)}
							{filters.cardStamps.map((cs) => (
								<Chip
									key={cs}
									icon={<CardStampIcon value={cs} />}
									label={`Tarjeta: ${getCardStampLabel(cs)}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										set(
											'cardStamps',
											filters.cardStamps.filter((x) => x !== cs),
										)
									}
								/>
							))}
							{filters.saleOrderId.trim() && (
								<Chip
									label={`Orden: ${filters.saleOrderId.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('saleOrderId', '')}
								/>
							)}
							{filters.receiptNumber.trim() && (
								<Chip
									label={`Recibo: ${filters.receiptNumber.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('receiptNumber', '')}
								/>
							)}
							{filters.passengerName.trim() && (
								<Chip
									label={`Pax: ${filters.passengerName.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('passengerName', '')}
								/>
							)}
							{filters.reference.trim() && (
								<Chip
									label={`Ref: ${filters.reference.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('reference', '')}
								/>
							)}
							{filters.observations.trim() && (
								<Chip
									label={`Obs.: ${filters.observations.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('observations', '')}
								/>
							)}
							{filters.amountFrom.trim() && (
								<Chip
									label={`USD ≥ ${filters.amountFrom}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('amountFrom', '')}
								/>
							)}
							{filters.amountTo.trim() && (
								<Chip
									label={`USD ≤ ${filters.amountTo}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('amountTo', '')}
								/>
							)}
							{filters.reconciledFrom && (
								<Chip
									label={`Concil. desde: ${format(filters.reconciledFrom, 'dd/MM/yy')}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('reconciledFrom', null)}
								/>
							)}
							{filters.reconciledTo && (
								<Chip
									label={`Concil. hasta: ${format(filters.reconciledTo, 'dd/MM/yy')}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('reconciledTo', null)}
								/>
							)}
							{filters.reconciliation !== 'all' && (
								<Chip
									label={filters.reconciliation === 'reconciled' ? 'Conciliados' : 'Sin conciliar'}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('reconciliation', 'all')}
								/>
							)}
							{filters.receipt !== 'all' && (
								<Chip
									label={filters.receipt === 'yes' ? 'Emitido' : 'Pendiente'}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('receipt', 'all')}
								/>
							)}
						</Box>
					)}

					<Box display="flex" gap={1}>
						<Tooltip title="Limpiar todos los filtros">
							<Button size="small" startIcon={<ClearIcon />} onClick={handleClear} variant="outlined" color="error">
								Limpiar
							</Button>
						</Tooltip>
						<Tooltip title={expanded ? 'Ocultar filtros avanzados' : 'Mostrar más filtros'}>
							<Button
								size="small"
								endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								onClick={() => setExpanded((prev) => !prev)}
								variant="outlined"
								color="primary"
							>
								{expanded ? 'Menos filtros' : 'Más filtros'}
							</Button>
						</Tooltip>
					</Box>
				</Box>

				{/* ── Filtros visibles ── */}
				<Grid container spacing={1.5}>
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							fullWidth
							size="small"
							label="Caso (Deal)"
							placeholder="ID numérico"
							value={filters.dealId}
							onChange={(e) => set('dealId', e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Creación desde"
							value={filters.dateFrom}
							onChange={(d) => handleCreationDateChange('dateFrom', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Creación hasta"
							value={filters.dateTo}
							onChange={(d) => handleCreationDateChange('dateTo', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						{buAutocomplete}
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							options={SEGMENT_OPTIONS}
							value={filters.segments}
							getOptionLabel={(o) => o.label}
							isOptionEqualToValue={(o, v) => o.id === v.id}
							onChange={(_, val) => set('segments', val)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
								))
							}
							renderInput={(params) => <TextField {...params} label="Segmento" size="small" />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							options={RELATION_OPTIONS}
							value={filters.relationTypes}
							getOptionLabel={(o) => o.label}
							isOptionEqualToValue={(o, v) => o.id === v.id}
							onChange={(_, val) => set('relationTypes', val)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
								))
							}
							renderInput={(params) => <TextField {...params} label="Tipo" size="small" />}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							options={PLATFORM_OPTIONS}
							value={filters.platformTypes}
							getOptionLabel={(o) => o.label}
							isOptionEqualToValue={(o, v) => o.id === v.id}
							onChange={(_, val) => set('platformTypes', val)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
								))
							}
							renderInput={(params) => <TextField {...params} label="Plataforma" size="small" />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							options={CURRENCY_OPTIONS}
							value={filters.currencies}
							getOptionLabel={(o) => o}
							onChange={(_, val) => set('currencies', val)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option} label={option} size="small" />
								))
							}
							renderInput={(params) => <TextField {...params} label="Moneda" size="small" />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							fullWidth
							size="small"
							label="Banco"
							placeholder="Santander…"
							value={filters.bankName}
							onChange={(e) => set('bankName', e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						{cardStampAutocomplete}
					</Grid>
				</Grid>

				<Collapse in={expanded}>
					<Divider sx={{ my: 1.5 }} />
					<Grid container spacing={1.5}>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Orden de venta"
								placeholder="ID de la orden"
								value={filters.saleOrderId}
								onChange={(e) => set('saleOrderId', e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Nro. recibo"
								placeholder="2026-00042"
								value={filters.receiptNumber}
								onChange={(e) => set('receiptNumber', e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Pasajero"
								placeholder="Nombre o apellido"
								value={filters.passengerName}
								onChange={(e) => set('passengerName', e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Auth ID / Referencia"
								placeholder="Código o ref."
								value={filters.reference}
								onChange={(e) => set('reference', e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<DebouncedTextField
								label="Observaciones"
								placeholder="Contiene…"
								value={filters.observations}
								onChange={(val) => set('observations', val)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<DatePicker
								label="Conciliación desde"
								value={filters.reconciledFrom}
								onChange={(d) => set('reconciledFrom', d)}
								inputFormat="dd/MM/yyyy"
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<DatePicker
								label="Conciliación hasta"
								value={filters.reconciledTo}
								onChange={(d) => set('reconciledTo', d)}
								inputFormat="dd/MM/yyyy"
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								label="Monto USD desde"
								placeholder="0.00"
								type="number"
								inputProps={{ min: 0, step: 0.01 }}
								value={filters.amountFrom}
								onChange={(e) => set('amountFrom', e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								label="Monto USD hasta"
								placeholder="9999.99"
								type="number"
								inputProps={{ min: 0, step: 0.01 }}
								value={filters.amountTo}
								onChange={(e) => set('amountTo', e.target.value)}
							/>
						</Grid>
					</Grid>
				</Collapse>

				<Divider sx={{ my: 1.5 }} />

				<Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
					<Chip
						label="Conciliados"
						size="small"
						variant={filters.reconciliation === 'reconciled' ? 'filled' : 'outlined'}
						color={filters.reconciliation === 'reconciled' ? 'success' : 'default'}
						onClick={() => set('reconciliation', filters.reconciliation === 'reconciled' ? 'all' : 'reconciled')}
						sx={{ cursor: 'pointer' }}
					/>
					<Chip
						label="Sin conciliar"
						size="small"
						variant={filters.reconciliation === 'unreconciled' ? 'filled' : 'outlined'}
						color={filters.reconciliation === 'unreconciled' ? 'warning' : 'default'}
						onClick={() => set('reconciliation', filters.reconciliation === 'unreconciled' ? 'all' : 'unreconciled')}
						sx={{ cursor: 'pointer' }}
					/>
					<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
					<Chip
						label="Emitido"
						size="small"
						variant={filters.receipt === 'yes' ? 'filled' : 'outlined'}
						color={filters.receipt === 'yes' ? 'info' : 'default'}
						onClick={() => set('receipt', filters.receipt === 'yes' ? 'all' : 'yes')}
						sx={{ cursor: 'pointer' }}
					/>
					<Chip
						label="Pendiente"
						size="small"
						variant={filters.receipt === 'no' ? 'filled' : 'outlined'}
						color={filters.receipt === 'no' ? 'warning' : 'default'}
						onClick={() => set('receipt', filters.receipt === 'no' ? 'all' : 'no')}
						sx={{ cursor: 'pointer' }}
					/>
				</Box>
			</Paper>
		</LocalizationProvider>
	);
};
